import {
  useMemo,
  useEffect,
  useReducer,
  useCallback,
  useState,
  useRef,
} from "react";

import axios, { endpoints } from "src/utils/axios";

import { AuthContext } from "./auth-context";
import { isValidToken } from "./utils";
import {
  AuthUserType,
  ActionMapType,
  AuthStateType,
  AuthRegistrationType,
} from "../../types";
import { jwtDecode } from "jwt-decode";
import AuthExpirationDialog from "src/components/auth-expiration-dialog/auth-expiration-dialog";
import { useBoolean } from "src/hooks/use-boolean";
import { paths } from "src/routes/paths";
import { useRouter } from "src/routes/hooks";
import ThemeProvider from "src/theme";
import { SettingsProvider } from "src/components/settings";
import { IUserItem } from "src/types/user";

// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

enum Types {
  INITIAL = "INITIAL",
  LOGIN = "LOGIN",
  REGISTER = "REGISTER",
  LOGOUT = "LOGOUT",
  UPDATE_PROVIDER = "UPDATE_PROVIDER",
}

type Payload = {
  [Types.INITIAL]: {
    user: AuthUserType;
  };
  [Types.LOGIN]: {
    user: AuthUserType;
  };
  [Types.REGISTER]: {
    user: AuthUserType;
  };
  [Types.LOGOUT]: undefined;
  [Types.UPDATE_PROVIDER]: {
    providerId: number;
    providerType: string;
  };
};

type ActionsType = ActionMapType<Payload>[keyof ActionMapType<Payload>];

// ----------------------------------------------------------------------

const initialState: AuthStateType = {
  user: null,
  loading: true,
};

const reducer = (state: AuthStateType, action: ActionsType) => {
  if (action.type === Types.INITIAL) {
    return {
      loading: false,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGIN) {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === Types.REGISTER) {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGOUT) {
    return {
      ...state,
      user: null,
    };
  }
  if (action.type === Types.UPDATE_PROVIDER) {
    return {
      ...state,
      currentProvider: action.payload.providerId,
      currentProviderType: action.payload.providerType,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

export const STORAGE_KEY = "accessToken";
// Create a ref to manage timers

type Props = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const dialog = useBoolean();
  const router = useRouter();

  const countDownSeconds = 30;
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const [currentProvider, setCurrentProvider] = useState(() => {
    // Check if a providerId exists in localStorage
    const savedProvider = localStorage.getItem("currentProvider");
    return savedProvider
      ? parseInt(savedProvider, 10)
      : state.user?.providers?.[0]?.id || null;
  });

  const [currentProviderType, setCurrentProviderType] = useState(() => {
    // Check if a currentProviderType exists in localStorage
    const savedProvider = localStorage.getItem("currentProviderType");
    return savedProvider
      ? savedProvider
      : state.user?.providers?.[0]?.type || "";
  });

  useEffect(() => {
    const syncAuthState = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY) {
        const accessToken = localStorage.getItem(STORAGE_KEY);

        if (accessToken && isValidToken(accessToken)) {
          setSession(accessToken);

          // Optionally fetch the user again
          axios.get(endpoints.auth.me).then((res) => {
            const { user } = res.data;
            dispatch({
              type: Types.LOGIN,
              payload: {
                user: {
                  ...user,
                  accessToken,
                },
              },
            });
          });
        } else {
          setSession(null);
          dispatch({ type: Types.LOGOUT });
        }
      }
    };

    const interval = setInterval(
      () => {
        refreshSession();
      },
      15 * 60 * 1000
    ); // Refresh every 15 minutes

    window.addEventListener("storage", syncAuthState);
    return () => {
      window.removeEventListener("storage", syncAuthState);
      clearInterval(interval);
    };
  }, []);

  const initialize = useCallback(async () => {
    try {
      const accessToken = localStorage.getItem(STORAGE_KEY);

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken);

        const res = await axios.get(endpoints.auth.me);

        const { user } = res.data;
        checkTokenExpiration(accessToken);
        dispatch({
          type: Types.INITIAL,
          payload: {
            user: {
              ...user,
              accessToken,
            },
          },
        });
      } else {
        dispatch({
          type: Types.INITIAL,
          payload: {
            user: null,
          },
        });
      }
    } catch (error) {
      console.error(error);
      dispatch({
        type: Types.INITIAL,
        payload: {
          user: null,
        },
      });

      logout();
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    return () => {
      // Cleanup on component unmount
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const setSession = (accessToken: string | null, checktoken = true) => {
    if (accessToken) {
      localStorage.setItem("accessToken", accessToken);

      axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

      // This function below will handle when token is expired
      if (checktoken) {
        checkTokenExpiration(accessToken);
      }
    } else {
      localStorage.removeItem("accessToken");

      delete axios.defaults.headers.common.Authorization;
    }
  };
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const logout = useCallback(async () => {
    console.log("logout");
    setSession(null);
    localStorage.removeItem("currentProvider"); // Clear the provider from localStorage
    localStorage.removeItem("currentProviderType"); // Clear the provider type from localStorage
    localStorage.removeItem(STORAGE_KEY); // Sync logout state
    dialog.onFalse(); // Close dialog if open
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    router.push(paths.auth.jwt.login);

    dispatch({
      type: Types.LOGOUT,
    });
  }, []);

  function startAutoLogoutTimer() {
    console.log("Starting auto-logout timer");

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // Start a timer for automatic logout after 30 seconds
    timerRef.current = setTimeout(() => {
      console.log("Executing auto-logout");
      dialog.onFalse(); // Close dialog if open
      /*localStorage.removeItem("accessToken");*/
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      setSession(null);
      //router.push(paths.auth.jwt.login);
      //router.reload();
      window.location.href = paths.auth.jwt.login;
    }, countDownSeconds * 1000); // 30 seconds after dialog opens
  }

  function checkTokenExpiration(accessToken: string) {
    try {
      const decoded = jwtDecode<{ exp?: number }>(accessToken);
      if (!decoded?.exp) return;

      const currentTime = Math.floor(Date.now() / 1000);
      const timeLeft = decoded.exp - currentTime;

      if (timeLeft <= 0) {
        console.warn("Token already expired");
        return;
      }

      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      timerRef.current = setTimeout(
        () => {
          dialog.onTrue();
          startAutoLogoutTimer();
        },
        Math.max(0, (timeLeft - countDownSeconds) * 1000)
      );
    } catch (e) {
      console.error("Token decode error", e);
    }
  }
  // function checkTokenExpiration(accessToken: string) {
  //   const decoded = jwtDecode<{ exp: number }>(accessToken);
  //   const currentTime = Math.floor(Date.now() / 1000);
  //   const timeLeft = decoded.exp - currentTime;

  //   console.log("Time left before expiration:", timeLeft);

  //   if (timeLeft > 0) {
  //     //if (timeLeft <= 0) {
  //     // Clear any existing timer

  //     if (timerRef.current) {
  //       console.log("Clearing existing timer");
  //       clearTimeout(timerRef.current);
  //       timerRef.current = null;
  //     }

  //     // Set a new timer to show the dialog 30 seconds before expiration
  //     timerRef.current = setTimeout(
  //       () => {
  //         console.log("Opening expiration dialog");
  //         dialog.onTrue(); // Open the expiration dialog
  //         startAutoLogoutTimer(); // Start logout countdown
  //       },
  //       //Math.max(0, (timeLeft - 50) * 1000)
  //       Math.max(0, (timeLeft - countDownSeconds) * 1000)
  //     ); // Ensure no negative delay
  //   }
  // }

  const refreshSession = useCallback(async () => {
    try {
      const accessTokenCheck = localStorage.getItem(STORAGE_KEY);
      if (!accessTokenCheck) return;

      // Decode token and check remaining time
      const decoded = jwtDecode<{ exp: number }>(accessTokenCheck);
      const currentTime = Math.floor(Date.now() / 1000);
      const timeLeft = decoded.exp - currentTime;
      console.log("Refreshing session", state, timeLeft);
      // Only refresh if the token has less than, say, 60 seconds remaining
      if (timeLeft > 60 && state?.user?.email) {
        console.log(
          "Token is still valid for",
          timeLeft,
          "seconds. Skipping refresh."
        );
        return;
      }

      const res = await axios.post(endpoints.auth.refresh, null, {
        headers: {
          Authorization: `Bearer ${accessTokenCheck}`,
        },
      });
      const { accessToken } = res.data;

      const userRes = await axios.get(endpoints.auth.me); // Fetch user details
      const updatedUser = userRes.data;

      // Update session and storage
      const checktoken = updatedUser?.id > 0 ? false : true;
      setSession(accessToken, checktoken);
      localStorage.setItem(STORAGE_KEY, accessToken);

      // Close the dialog if open
      dialog.onFalse();

      // Clear the auto-logout timer
      if (timerRef.current) {
        console.log("Clearing auto-logout timer");
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      // Update the auth state and restart the expiration check
      dispatch({
        type: Types.LOGIN,
        payload: {
          user: {
            ...updatedUser,
            accessToken,
          },
        },
      });

      if (!updatedUser?.id) {
        checkTokenExpiration(accessToken); // Restart expiration timer
      }
    } catch (error) {
      console.error("Failed to refresh token:", error);
      logout(); // Log out if refresh fails
    }
  }, [state.user, logout]);

  // LOGIN
  const login = useCallback(async (email: string, password: string) => {
    const data = {
      email,
      password,
    };
    setIsLoggingIn(true);

    const res = await axios.post(endpoints.auth.login, data);

    //const { accessToken, user } = res.data;
    const { accessToken } = res.data;

    setSession(accessToken);
    localStorage.setItem(STORAGE_KEY, accessToken); // Sync login state

    // Fetch the latest user data
    const userRes = await axios.get(endpoints.auth.me);

    const updatedUser = userRes.data;

    setIsLoggingIn(false);

    dispatch({
      type: Types.LOGIN,
      payload: {
        user: {
          ...updatedUser,
          accessToken,
        },
      },
    });
  }, []);

  const loginWithAccessToken = useCallback(async (accessToken: string) => {
    setIsLoggingIn(true);

    // 1) Store FS token in axios + localStorage
    setSession(accessToken);
    localStorage.setItem(STORAGE_KEY, accessToken);

    // 2) Fetch user via /me
    const userRes = await axios.get(endpoints.auth.me);
    const updatedUser = userRes.data;

    setIsLoggingIn(false);

    // 3) Update auth state
    dispatch({
      type: Types.LOGIN,
      payload: {
        user: {
          ...updatedUser,
          accessToken,
        },
      },
    });
  }, []);

  const updateUser = useCallback(async (newData: Partial<IUserItem>) => {
    try {
      const res = await axios.put(endpoints.auth.update, newData);
      const updatedUser = res.data;

      dispatch({
        type: Types.LOGIN,
        payload: {
          ...state.user, // Preserve accessToken and other fields
          user: updatedUser,
        },
      });
    } catch (error) {
      console.error("Failed to update user:", error);
    }
  }, []);

  //REGISTRATION
  const register = useCallback(async (data: AuthRegistrationType) => {
    const postdata = {
      email: data.email,
      password: data.password,
      first_name: data.firstName,
      last_name: data.lastName,
      phone: data.phone,
      providers: data.providers,
      lang: data.lang,

      placenet_token: data.placenet_token || null,
    };

    const resRegister = await axios.post(endpoints.auth.register, postdata);

    const res = await axios.post(endpoints.auth.login, {
      email: resRegister.data.email,
      password: resRegister.data.password,
    });

    const { accessToken, user } = res.data;

    localStorage.setItem(STORAGE_KEY, accessToken);

    dispatch({
      type: Types.LOGIN,
      payload: {
        ...state.user,
        user,
      },
    });

    if (data.providers && data.providers.length) {
      window.location.href = paths.dashboard.root;
    } else {
      const url = new URL(window.location.href);

      if (window.location.href.includes("/sportmates")) {
        const code = url.searchParams.get("code");

        if (code) {
          window.location.href =
            paths.sportmates.root + "/?subscription=1&code=" + code;
        } else {
          window.location.href = paths.sportmates.root + "/?subscription=1";
        }
      } else if (window.location.href.includes("/invitations")) {
        const invitation_type = url.searchParams.get("type");
        window.location.href = "/invitations?type=" + invitation_type;
      } else {
        window.location.href = paths.player.profiledata + "?submenu=billing";
      }
    }

    return res?.data?.user;
  }, []);

  // ADD NEW PROVIDER ROLE
  const addProviderRole = useCallback(async (providers: string[]) => {
    //await axios.post(endpoints.provider.create, { providers });
    try {
      const response = await axios.post(endpoints.provider.create, {
        providers,
      });
      return response;
    } catch (error) {}
  }, []);

  // CONFIRM REGISTER
  const confirmRegister = useCallback(async (email: string, code: string) => {
    await axios.post(endpoints.auth.verifyemail, { email, code });
  }, []);

  // FORGOT PASSWORD
  const forgotPassword = useCallback(async (email: string) => {
    await axios.post(endpoints.auth.forgotPassword, { email });
  }, []);

  // NEW PASSWORD
  const newPassword = useCallback(
    async (email: string, code: string, password: string) => {
      await axios.post(endpoints.auth.forgotPasswordSubmit, {
        email,
        code,
        password,
      });
    },
    []
  );

  // Add updateProvider function in AuthProvider
  const updateProvider = (providerId: number, providerType: string) => {
    // Save the providerId to localStorage
    setCurrentProvider(providerId);
    setCurrentProviderType(providerType);
    localStorage.setItem("currentProvider", providerId.toString());
    localStorage.setItem("currentProviderType", providerType);
  };

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? "authenticated" : "unauthenticated";

  const status = state.loading ? "loading" : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      method: "jwt",
      loading: status === "loading",
      authenticated: status === "authenticated",
      unauthenticated: status === "unauthenticated",
      currentProvider: parseInt(
        localStorage.getItem("currentProvider") || "0",
        10
      ),
      currentProviderType: localStorage.getItem("currentProviderType") || "",
      isLoggingIn,
      //
      login,
      loginWithAccessToken,
      register,
      logout,
      updateUser, // Add the updateUser function
      refreshSession,
      confirmRegister,
      forgotPassword,
      newPassword,
      addProviderRole,
      updateProvider,
      checkTokenExpiration,
    }),
    [
      login,
      loginWithAccessToken,
      logout,
      register,
      state.user,
      status,
      updateUser,
      refreshSession,
      confirmRegister,
      forgotPassword,
      newPassword,
      addProviderRole,
      currentProvider,
      currentProviderType,
      updateProvider,
      checkTokenExpiration,
      isLoggingIn,
    ]
  );

  return (
    <AuthContext.Provider value={memoizedValue}>
      <SettingsProvider
        defaultSettings={{
          themeMode: "light", // 'light' | 'dark'
          themeDirection: "ltr", //  'rtl' | 'ltr'
          themeContrast: "default", // 'default' | 'bold'
          themeLayout: "vertical", // 'vertical' | 'horizontal' | 'mini'
          themeColorPresets: "default", // 'default' | 'cyan' | 'purple' | 'blue' | 'orange' | 'red'
          themeStretch: false,
        }}
      >
        <ThemeProvider>
          <AuthExpirationDialog
            open={dialog.value}
            onClose={dialog.onFalse}
            onStayLoggedIn={refreshSession}
            onLogOut={logout}
          />
        </ThemeProvider>
      </SettingsProvider>
      {children}
    </AuthContext.Provider>
  );
}
